import { Box, chakra, Flex, Heading, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import * as m from 'motion/react-m';

import { DiscordIcon } from '#features/ui';

const DISCORD_URL = 'https://discord.gg/gt56cQ4nWb';

/**
 * Community join near the foot of the page - soft social proof for early stage
 * ("growing community") plus the built-by-gamers authenticity line. Kept low in
 * the funnel on purpose so it doesn't pull visitors off to Discord too early.
 */
export const DiscordCommunity = () => {
  const { t } = useLingui();

  return (
    <Box as="section" aria-label="Community" w="100%">
      <Flex
        direction={{ base: 'column', md: 'row' }}
        alignItems="center"
        gap={{ base: 5, md: 8 }}
        p={{ base: 6, md: 8 }}
        borderRadius="2xl"
        border="1px solid"
        borderColor="whiteAlpha.100"
        background="linear-gradient(165deg, hsla(228, 11.6%, 14%, 0.6) 0%, hsla(228, 11.6%, 10%, 0.4) 100%)"
        asChild
      >
        <m.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Flex
            w={{ base: '56px', md: '64px' }}
            h={{ base: '56px', md: '64px' }}
            borderRadius="2xl"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
            color="white"
            fontSize="2xl"
            style={{ background: 'linear-gradient(135deg, #5865F2 0%, #4048c4 100%)', boxShadow: '0 10px 30px -8px rgba(88,101,242,0.7)' }}
          >
            <DiscordIcon />
          </Flex>

          <Box flex="1" textAlign={{ base: 'center', md: 'left' }}>
            <Heading as="h2" fontWeight="900" fontSize={{ base: 'xl', md: '2xl' }} lineHeight="1.15" mb={1.5}>
              {t`v3.discord.title`}
            </Heading>
            <Text fontSize="sm" color="fg.muted" lineHeight="1.6" maxW="440px" mx={{ base: 'auto', md: 0 }}>
              {t`v3.discord.subtitle`}
            </Text>
          </Box>

          <chakra.a
            href={DISCORD_URL}
            target="_blank"
            rel="noopener noreferrer"
            flexShrink={0}
            px={6}
            py={3}
            borderRadius="xl"
            fontWeight="800"
            fontSize="sm"
            color="white"
            whiteSpace="nowrap"
            transition="all 0.2s"
            _hover={{ transform: 'translateY(-2px)', filter: 'brightness(1.08)' }}
            style={{ background: 'linear-gradient(135deg, #5865F2 0%, #4048c4 100%)' }}
          >
            {t`v3.discord.button`}
          </chakra.a>
        </m.div>
      </Flex>

      {/* Built-by-gamers authenticity line */}
      <Flex justifyContent="center" mt={5}>
        <Text fontSize="xs" fontWeight="600" color="fg.subtle" letterSpacing="0.06em" textTransform="uppercase">
          {t`v3.builtBy`}
        </Text>
      </Flex>
    </Box>
  );
};
